import { useCallback, useEffect, useState } from "react";
import { ScrollText, Download, Filter, ShieldAlert, LogIn, Lock, KeyRound, Ban, RefreshCw } from "lucide-react";
import { useAuth } from "../contexts/AuthContext";
import PageHeader from "../components/PageHeader";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";

const SEV = { info: "text-slate-300 bg-white/5", warning: "text-amber-300 bg-amber-500/10", critical: "text-red-400 bg-red-500/10" };

export default function AccessAuditPage() {
  const { axiosAuth } = useAuth();
  const api = axiosAuth();
  const [stats, setStats] = useState(null);
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [types, setTypes] = useState({});
  const [filters, setFilters] = useState({ event_type: "", email: "", severity: "", date_from: "", date_to: "", success: "" });
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = Object.fromEntries(Object.entries(filters).filter(([, v]) => v));
      const [s, l] = await Promise.all([api.get("/admin/access-audit/stats"), api.get("/admin/access-audit", { params: { ...params, limit: 300 } })]);
      setStats(s.data); setItems(l.data.items); setTotal(l.data.total); setTypes(l.data.event_types || {});
    } catch (e) { console.error(e); }
    setLoading(false);
  }, [filters]); // eslint-disable-line
  useEffect(() => { load(); }, [load]);

  const exportCsv = async () => {
    const params = Object.fromEntries(Object.entries(filters).filter(([, v]) => v));
    const r = await api.get("/admin/access-audit/export.csv", { params, responseType: "blob" });
    const url = window.URL.createObjectURL(new Blob([r.data]));
    const a = document.createElement("a"); a.href = url; a.download = "audit_acces.csv"; a.click(); window.URL.revokeObjectURL(url);
  };

  const set = (k, v) => setFilters(f => ({ ...f, [k]: v }));
  const SEL = "bg-[#0b0811] border border-white/10 text-white rounded-lg px-2 h-9 text-xs";

  return (
    <div className="p-6 max-w-7xl mx-auto" data-testid="access-audit-page">
      <PageHeader title="Journal d'audit des accès" subtitle="Connexions, échecs, verrouillages, double authentification et refus d'accès — qui, quand, depuis où." icon={<ScrollText />} />

      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 xl:grid-cols-7 gap-3 mb-6" data-testid="audit-stats">
          {[["Connexions 24 h", stats.logins_24h, LogIn, "#22c55e"], ["Échecs 24 h", stats.failures_24h, ShieldAlert, "#f59e0b"], ["Verrouillages 24 h", stats.locks_24h, Lock, "#ef4444"],
            ["2FA refusées 24 h", stats.twofa_failed_24h, KeyRound, "#f97316"], ["Accès refusés 24 h", stats.denied_24h, Ban, "#ef4444"], ["Verrous actifs", stats.currently_locked, Lock, "#f7c935"], ["Comptes avec 2FA", stats.users_with_2fa, KeyRound, "#38bdf8"]].map(([l, v, Icon, c]) => (
            <div key={l} className="bg-[#14101c] border border-white/5 rounded-xl p-3">
              <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wide text-slate-500"><Icon className="w-3 h-3" style={{ color: c }} /> {l}</div>
              <div className="text-2xl font-black mt-1" style={{ color: c }}>{v}</div>
            </div>
          ))}
        </div>
      )}

      {stats && (stats.top_ips?.length > 0 || stats.top_emails?.length > 0) && (
        <div className="grid md:grid-cols-2 gap-3 mb-6 text-xs">
          <div className="bg-[#14101c] border border-white/5 rounded-xl p-4" data-testid="audit-top-ips">
            <div className="font-bold text-slate-300 mb-2">IP avec le plus d'échecs (7 j)</div>
            {stats.top_ips.map(t => <div key={t.ip} className="flex justify-between py-1 border-b border-white/5 last:border-0"><span className="font-mono text-slate-400">{t.ip}</span><span className="text-amber-400 font-bold">{t.failures}</span></div>)}
          </div>
          <div className="bg-[#14101c] border border-white/5 rounded-xl p-4" data-testid="audit-top-emails">
            <div className="font-bold text-slate-300 mb-2">Comptes ciblés (7 j)</div>
            {stats.top_emails.map(t => <div key={t.email} className="flex justify-between py-1 border-b border-white/5 last:border-0"><span className="text-slate-400">{t.email}</span><span className="text-amber-400 font-bold">{t.failures}</span></div>)}
          </div>
        </div>
      )}

      <div className="flex flex-wrap items-end gap-2 mb-3" data-testid="audit-filters">
        <Filter className="w-4 h-4 text-slate-500 mb-2" />
        <select value={filters.event_type} onChange={e => set("event_type", e.target.value)} className={SEL} data-testid="audit-filter-type">
          <option value="">Tous les événements</option>
          {Object.entries(types).map(([k, l]) => <option key={k} value={k}>{l}</option>)}
        </select>
        <select value={filters.severity} onChange={e => set("severity", e.target.value)} className={SEL} data-testid="audit-filter-severity">
          <option value="">Toute gravité</option><option value="info">Info</option><option value="warning">Avertissement</option><option value="critical">Critique</option>
        </select>
        <select value={filters.success} onChange={e => set("success", e.target.value)} className={SEL}>
          <option value="">Succès et échecs</option><option value="true">Succès</option><option value="false">Échecs</option>
        </select>
        <Input value={filters.email} onChange={e => set("email", e.target.value)} placeholder="E-mail…" className="bg-[#0b0811] border-white/10 text-white h-9 text-xs w-48" data-testid="audit-filter-email" />
        <Input type="date" value={filters.date_from} onChange={e => set("date_from", e.target.value)} className="bg-[#0b0811] border-white/10 text-white h-9 text-xs w-36" />
        <Input type="date" value={filters.date_to} onChange={e => set("date_to", e.target.value)} className="bg-[#0b0811] border-white/10 text-white h-9 text-xs w-36" />
        <Button size="sm" variant="outline" onClick={load} className="border-white/10 text-slate-300 h-9 text-xs"><RefreshCw className="w-3.5 h-3.5 mr-1" /> Actualiser</Button>
        <Button size="sm" onClick={exportCsv} className="bg-[#f7c935] text-[#14101c] hover:bg-[#e6b825] h-9 text-xs font-bold ml-auto" data-testid="audit-export-btn"><Download className="w-3.5 h-3.5 mr-1" /> Export CSV</Button>
      </div>

      <div className="bg-[#14101c] border border-white/5 rounded-xl overflow-hidden">
        <div className="px-4 py-2 text-[11px] text-slate-500 border-b border-white/5">{total} événement(s){loading ? " — chargement…" : ""}</div>
        <div className="overflow-x-auto">
          <table className="w-full text-xs" data-testid="audit-table">
            <thead className="text-[10px] uppercase text-slate-500 bg-[#0b0811]">
              <tr><th className="p-2 text-left">Date</th><th className="p-2 text-left">Événement</th><th className="p-2 text-left">Utilisateur</th><th className="p-2 text-left">IP</th><th className="p-2 text-left">Détails</th><th className="p-2 text-left">Navigateur</th></tr>
            </thead>
            <tbody>
              {items.map((it, i) => (
                <tr key={i} className="border-t border-white/5 hover:bg-white/[0.02]" data-testid="audit-row">
                  <td className="p-2 whitespace-nowrap text-slate-400">{new Date(it.at).toLocaleString("fr-FR")}</td>
                  <td className="p-2"><span className={`px-1.5 py-0.5 rounded text-[10px] font-bold ${SEV[it.severity] || SEV.info}`}>{it.label}</span>{!it.success && <span className="ml-1 text-[9px] text-red-400">échec</span>}</td>
                  <td className="p-2"><div className="text-slate-200">{it.name || "—"}</div><div className="text-slate-500">{it.email}{it.role ? ` · ${it.role}` : ""}</div></td>
                  <td className="p-2 font-mono text-slate-400">{it.ip}</td>
                  <td className="p-2 text-slate-400 max-w-[260px] truncate" title={it.details}>{it.details}</td>
                  <td className="p-2 text-slate-600 max-w-[200px] truncate" title={it.user_agent}>{it.user_agent}</td>
                </tr>
              ))}
              {!loading && items.length === 0 && <tr><td colSpan={6} className="p-8 text-center text-slate-600">Aucun événement pour ces filtres</td></tr>}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
